import React from 'react';

// Normalize for matching: lowercase, strip accents + punctuation, collapse ws.
const norm = (s) => String(s || '')
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

// Split a combined "A & B" / "A and B" / "A, B & C" name into member parts.
const splitParts = (name) => String(name || '')
    .split(/\s*&\s*|\s*,\s*|\s+and\s+/i)
    .map(norm).filter(Boolean);

// Display-only de-duplication for tracks credited to BOTH a combined act and its
// members (e.g. "Roni Size & Reprazent" + "Roni Size" + "Reprazent"). The choice
// of WHICH to show is NOT guessed on the client — it's driven by the backend
// `showAsUnit` flag on the combined artist (set by the composite-oversplit pass):
//   showAsUnit === true  → real act  → show the combined, hide its members
//   showAsUnit === false → collab    → hide the combined, show its members
//   showAsUnit absent    → no decision → render as-is (never guess)
// All ids stay in artist_ids upstream, so support/credit is always preserved —
// this only collapses redundant chips at render time.
export function dedupeCombinedArtists(list) {
    if (!Array.isArray(list) || list.length < 2) return list || [];
    const normNames = list.map(a => norm(a.name));
    const hidden = new Set();
    list.forEach((a, i) => {
        const flag = a && a.showAsUnit;
        if (flag !== true && flag !== false) return; // no backend decision → leave as-is
        const parts = splitParts(a.name);
        if (parts.length < 2) return; // not a combined name
        // members of THIS combined that are also present in the list
        const memberIdx = [];
        list.forEach((b, j) => {
            if (i === j) return;
            if (normNames[j] && normNames[j] !== normNames[i] && parts.includes(normNames[j])) memberIdx.push(j);
        });
        if (flag === true) {
            memberIdx.forEach(j => hidden.add(j));      // show duo, hide members
        } else if (memberIdx.length > 0) {
            hidden.add(i);                              // show members, hide the combined
        }
    });
    return hidden.size ? list.filter((_, i) => !hidden.has(i)) : list;
}

// CLIENT HACK: placeholder credits that aren't real artists ("VA" on
// compilation mixes, "ID" / "Unknown Artist" on unidentified tracklist
// rows) must not render as links — their artist pages are meaningless.
// Proper fix is cleaning these out of the DB (planned); until then
// suppress the click client-side.
//
// Matching is exact on the normalized name (lowercase, accents/punctuation
// stripped — so "V.A." → "v a", "N/A" → "n a"; both spellings listed).
// NEVER loosen to substring/word matching, and keep this list to credits
// that can only mean "no specific artist": the DB audit (2026-08-11) found
// format-word garbage that COLLIDES with real acts — "edIT" (Glitch Mob)
// normalizes to "edit", "Promo" is a real hardcore artist, plus Secret /
// Intro / LIVE / Bootleg… Those belong to the server-side cleanup
// (db-scripts/clean_garbage_artists.js), not this list.
const NON_ARTIST_NAMES = new Set([
    'va', 'v a', 'various', 'various artists',                       // compilations
    'unknown', 'unknown artist', 'unknown dj', 'artist unknown',
    'no artist', 'unknown title',                                    // unidentified
    'id', 'i d', 'ids',                                              // DJ-set "ID - ID" convention
    'n a', 'na', 'tba', 'tbd', 'none', 'misc', 'untitled',           // explicit non-values
    'dj', 'guest', 'special guest',                                  // role, not identity
]);
// Empty-after-normalization ("?", "...") is placeholder too — nothing to search.
export const isNonArtistName = (name) => {
    const n = norm(name);
    return !n || NON_ARTIST_NAMES.has(n);
};

// Renders a list of artists where each name is independently hoverable/clickable.
// Backend-resolved `artists[]` carry ids and are the canonical source, so we
// ALWAYS render them when present — one clickable segment per resolved artist,
// navigating by id. This is what makes "&"/"and"-joined source strings (e.g.
// "Bad Boombox & mischluft", one raw text element but two resolved ids) split
// into separately-clickable artists. The `fallbackText` is only used when no ids
// resolved at all; there we comma-split it and click by name, since that's the
// only signal we have.
//
// Props:
//   artists?: Array<{ id, name, ... }>
//   fallbackText?: string
//   onSelect: ({id, name}) => void
//   className?: string         applied to outer span
//   segmentClassName?: string  applied to each clickable segment
//   separator?: string         default ", "
//   segmentWrapper?: (seg, node) => node   opt-in wrapper around each rendered
//       segment (e.g. a hover-preview card) — must render `node` inside
//   renderName?: (name) => node   opt-in display transform for the name text
//       (e.g. a filter-query highlighter) — affects display only, never matching
export default function MultiArtistText({
    artists,
    fallbackText,
    onSelect,
    className,
    segmentClassName,
    separator = ', ',
    segmentWrapper,
    renderName,
}) {
    const list = Array.isArray(artists) ? artists.filter(a => a && a.name) : [];

    const handle = (e, payload) => {
        e.stopPropagation();
        if (onSelect) onSelect(payload);
    };

    const renderSegments = (segments) => (
        <span className={className}>
            {segments.map((seg, i) => {
                // Placeholder credits render as inert text — no link, no hover card.
                const inert = isNonArtistName(seg.name);
                const node = (
                    <span
                        className={inert ? undefined : segmentClassName}
                        onClick={inert ? undefined : (e) => handle(e, seg)}
                    >
                        {renderName ? renderName(seg.name) : seg.name}
                    </span>
                );
                return (
                    <React.Fragment key={`${seg.id ?? 'x'}-${i}`}>
                        {i > 0 && separator}
                        {segmentWrapper && !inert ? segmentWrapper(seg, node) : node}
                    </React.Fragment>
                );
            })}
        </span>
    );

    // Resolved ids are canonical — always prefer them, one clickable per artist.
    // Collapse redundant member chips when the combined act is also present
    // ("Roni Size & Reprazent, Roni Size, Reprazent" → "Roni Size & Reprazent").
    if (list.length > 0) return renderSegments(dedupeCombinedArtists(list));

    // No ids resolved at all: fall back to the raw text, comma-split, click by name.
    const textSegments = typeof fallbackText === 'string' && fallbackText
        ? fallbackText.split(',').map(s => s.trim()).filter(Boolean)
        : [];
    if (textSegments.length > 0) {
        return renderSegments(
            textSegments.map(name => ({ id: null, name }))
        );
    }
    return null;
}
